import { Head, Link } from "@inertiajs/react"
import { Building2, MapPin, Pencil } from "lucide-react"

import ContentActionMenu from "@/components/content-action-menu"
import { Badge } from "@/components/ui/badge"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import VerifiedBadge from "@/components/verified-badge"
import AppLayout from "@/layouts/app-layout"
import type { ScoutsShow } from "@/types/serializers"

export default function ScoutShow({ scout, is_own_profile }: ScoutsShow) {
  const pageTitle = `${scout.user.full_name} - Scout | KrownFT`

  return (
    <AppLayout>
      <Head title={pageTitle} />

      <div className="min-h-svh">
        <div className="from-krown-200 to-krown-800 bg-gradient-to-br">
          <div className="mx-auto max-w-4xl px-6 pt-12 pb-8">
            <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-end">
              <Avatar className="border-background h-[120px] w-[120px] shrink-0 border-4 shadow-lg">
                <AvatarImage
                  src={scout.avatar_url ?? undefined}
                  alt={scout.user.full_name}
                  className="object-cover"
                />
                <AvatarFallback className="bg-krown-600 border-background text-2xl font-bold text-white">
                  <Building2 className="h-12 w-12 text-white/60" />
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 text-center sm:text-left">
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-white">
                      {scout.user.full_name}
                    </h1>
                    <p className="text-krown-50/70">@{scout.user.nickname}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {is_own_profile && (
                      <Button
                        variant="cta"
                        size="sm"
                        className="hover:bg-krown-600 hover:border-krown-600 hover:shadow-md"
                        asChild
                      >
                        <Link href="/perfil/editar">
                          <Pencil className="mr-1.5 h-4 w-4" />
                          Editar perfil
                        </Link>
                      </Button>
                    )}
                    <ContentActionMenu
                      reportableType="User"
                      reportableId={scout.user.id}
                      hideableType="User"
                      hideableId={scout.user.id}
                      showBlock
                      blockUserId={scout.user.id}
                    />
                  </div>
                </div>

                <div className="mt-2 flex flex-wrap items-center justify-center gap-2 sm:justify-start">
                  {scout.role_title && (
                    <Badge
                      variant="outline"
                      className="border-krown-accent bg-krown-accent/20 text-krown-accent"
                    >
                      {scout.role_title}
                    </Badge>
                  )}
                  <VerifiedBadge show={scout.verified} />
                  {(scout.city ?? scout.country) && (
                    <span className="text-krown-50/80 flex items-center gap-1 text-sm">
                      <MapPin className="h-3.5 w-3.5" />
                      {[scout.city, scout.country].filter(Boolean).join(", ")}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content area */}
        <div className="mx-auto max-w-4xl px-6 py-8">
          {/* Organization */}
          {scout.organization_name && (
            <section className="mb-8">
              <h2 className="mb-3 text-lg font-semibold">Organización</h2>
              <p className="text-muted-foreground">{scout.organization_name}</p>
            </section>
          )}

          {/* Bio */}
          <section className="mb-8">
            <h2 className="mb-3 text-lg font-semibold">Sobre mí</h2>
            {scout.bio ? (
              <p className="text-muted-foreground">{scout.bio}</p>
            ) : (
              <p className="text-muted-foreground">Sin información</p>
            )}
          </section>
        </div>
      </div>
    </AppLayout>
  )
}
